import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import Logo from '../components/ui/Logo';
import { APP_CONFIG } from '../data/constants.jsx';
import apiService from '../services/api';

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, loading } = useAuth();
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });

  // Get Google OAuth URL dynamically - computed immediately, no delay
  const googleAuthUrl = useMemo(() => {
    // Get the API base URL (same logic as apiService)
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;

    // Check for Coder environment
    if (hostname.includes('coder.') || hostname.includes('--')) {
      const backendHost = hostname.replace(/5174--/g, '8898--').replace(/5173--/g, '8898--');
      return `${protocol}//${backendHost}/api/accounts/google/login/`;
    }

    // Check for localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:8898/api/accounts/google/login/`;
    }

    // Production (relative URL through nginx)
    return '/api/accounts/google/login/';
  }, []); // Empty deps - compute once on mount


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setFormLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('🔄 Login: Starting login process');
      const result = await login({
        phone: formData.phone,
        password: formData.password,
      });
      console.log('📥 Login: Login result received:', result);

      if (result && result.success) {
        console.log('✅ Login: Login successful');
        setSuccess(t('auth.loginSuccess'));
        setFormData({ phone: '', password: '' });
        setTimeout(() => navigate('/profile'), 1000);
      } else {
        const errorMsg = result?.error || t('auth.loginError');
        console.log('❌ Login: Login failed, showing error:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('🚨 Login: Form submission error caught:', err);
      setError(err.message || t('common.errorGeneric'));
    } finally {
      setFormLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-3 mb-6 hover:opacity-80 transition-opacity">
            <Logo className="w-12 h-12 rounded-xl" />
            <div>
              <h1 className="font-bold text-2xl text-gray-800">{APP_CONFIG.name}</h1>
              <p className="text-sm text-gray-600">{t('footer.appNameBengali')}</p>
            </div>
          </Link>
          <p className="text-gray-600">
            {t('auth.loginRegisterPrompt')}
          </p>
        </div>

        <Card className="p-0 overflow-hidden">
          {/* Auth Tabs */}
          <div className="flex border-b border-gray-200">
            <div className="flex-1 text-center py-4 px-6 font-medium text-white bg-gradient-to-r from-green-500 to-blue-500 cursor-default rounded-tl-lg">
              {t('auth.login') || 'লগইন'}
            </div>
            <Link
              to="/register"
              className="flex-1 text-center py-4 px-6 font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors rounded-tr-lg"
            >
              {t('auth.register') || 'রেজিস্টার'}
            </Link>
          </div>

          <div className="p-8">
          {/* Success Message */}
          {success && (
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form className="space-y-4" noValidate>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.form.phone')} *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={t('auth.form.phonePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.form.password')} *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength="4"
                  className="w-full px-4 py-2 pr-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={t('auth.form.passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </div>

            <Button
              type="button"
              disabled={formLoading || loading}
              className="w-full"
              onClick={handleSubmit}
            >
              {formLoading || loading ? t('common.loading') : t('auth.login')}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('auth.orContinueWith') || 'অথবা'}</span>
            </div>
          </div>

          {/* Google Sign In Button */}
          <a
            href={googleAuthUrl}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-gray-700 font-medium"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.signInWithGoogle') || 'Google দিয়ে সাইন ইন করুন'}
          </a>

          {/* Back to Homepage */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <Link
              to="/"
              className="text-gray-600 hover:text-gray-800 text-sm transition-colors"
            >
              ← {t('nav.backToHome')}
            </Link>
          </div>
          </div>
        </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Login;